import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 2000;

export function useAutoSave(
  shouldTrigger: boolean,
  onSave: () => Promise<void>,
  onComplete: () => void,
  delay: number = DEFAULT_DELAY,
): { isAutoSaving: boolean } {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle auto-save trigger
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Only start timer if shouldTrigger is true
    if (!shouldTrigger) {
      return;
    }

    // Start debounce timer
    timeoutRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;

      setIsAutoSaving(true);
      try {
        await onSave();
        if (isMountedRef.current) {
          onComplete();
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        if (isMountedRef.current) {
          setIsAutoSaving(false);
        }
      }
    }, delay);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [shouldTrigger, onSave, onComplete, delay]);

  return { isAutoSaving };
}
